// Pure helpers used by DxReportEditor for shaping/inspecting payloads.
// Kept separate so the editor file stays focused on orchestration.

import { collectDocImageFileIds, applyResolvedUrlsToDoc } from '@odo/shared-ui';
import type { TimelineEvent } from '../../types';
import type { DxReportPayload } from '../types';
import type { TiptapDoc } from '@odo/shared-ui';

export function extractPayloadFromEvent(event: TimelineEvent): DxReportPayload | null {
    const payload = event.payload as Record<string, unknown> | null;
    const formData = payload?.formData;
    if (!formData || typeof formData !== 'object') return null;

    const topLevelReview = (payload as { review?: unknown }).review;
    const fdReview = (formData as { review?: unknown }).review;
    const existingReview =
        topLevelReview && typeof topLevelReview === 'object'
            ? topLevelReview
            : fdReview && typeof fdReview === 'object'
              ? fdReview
              : null;

    if (existingReview) {
        return { ...(formData as object), review: existingReview } as unknown as DxReportPayload;
    }

    return formData as unknown as DxReportPayload;
}

export function computeAge(dobIso: string | null): number | null {
    if (!dobIso) return null;
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) years -= 1;
    return years >= 0 ? years : null;
}

export function toDateOnly(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const match = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
}

/** Walks the payload's tiptap section docs and returns every distinct image
 *  file id. Covers plain `image` nodes (`data-file-id`) and `imageGridCell`
 *  nodes (`fileId`). Used to resolve signed URLs for resumed/edited drafts. */
export function collectImageFileIds(payload: DxReportPayload): string[] {
    const ids = new Set<string>();
    for (const section of payload.sections) {
        for (const id of collectDocImageFileIds(section.tiptapJson as TiptapDoc)) {
            ids.add(id);
        }
    }
    return [...ids];
}

/** Returns a new payload with every image node's `src` swapped for the
 *  freshly-resolved signed URL. Covers plain `image` nodes and `imageGridCell`
 *  nodes. Immutable — safe to set into React state. */
export function rewriteImageSrcs(
    payload: DxReportPayload,
    urls: Record<string, string>
): DxReportPayload {
    return {
        ...payload,
        sections: payload.sections.map((section) => ({
            ...section,
            tiptapJson: applyResolvedUrlsToDoc(section.tiptapJson as TiptapDoc, urls)
        }))
    };
}
